/**
 * Pure formatters for training-block payloads.
 *
 * Kept separate from the React layer so the same logic can be
 * exercised in unit tests without rendering a tree.
 */
import type {
  BlockStatus,
  TrainingBlockDay,
  TrainingBlockDetail,
  WorkoutType,
} from '../types/api.ts';

export function formatBlockStatus(status: BlockStatus): string {
  return {
    draft: 'Draft',
    active: 'Active',
    completed: 'Completed',
    abandoned: 'Abandoned',
  }[status];
}

export function blockStatusClass(status: BlockStatus): string {
  return {
    draft: 'bg-gray-500/15 text-gray-300 border-gray-500/40',
    active: 'bg-green-500/15 text-green-400 border-green-500/40',
    completed: 'bg-blue-500/15 text-blue-400 border-blue-500/40',
    abandoned: 'bg-red-500/15 text-red-400 border-red-500/40',
  }[status];
}

const _WORKOUT_LABELS: Record<WorkoutType, string> = {
  rest: 'Rest',
  recovery: 'Recovery',
  endurance: 'Endurance',
  long_ride: 'Long ride',
  tempo: 'Tempo',
  sweet_spot: 'Sweet spot',
  threshold: 'Threshold',
  vo2: 'VO2 max',
  anaerobic: 'Anaerobic',
  sprint: 'Sprint',
  intervals: 'Intervals',
  ftp_test: 'FTP test',
  ramp_test: 'Ramp test',
  test: 'Test',
  race: 'Race',
  group_ride: 'Group ride',
  skills: 'Skills',
  cross_training: 'Cross-training',
};

export function workoutLabel(type: WorkoutType): string {
  return _WORKOUT_LABELS[type] ?? type;
}

const _WORKOUT_CHIP: Record<WorkoutType, string> = {
  rest: 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border)',
  recovery: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  endurance: 'bg-green-500/15 text-green-400 border-green-500/40',
  long_ride: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
  tempo: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/40',
  sweet_spot: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
  threshold: 'bg-orange-500/15 text-orange-400 border-orange-500/40',
  vo2: 'bg-red-500/15 text-red-400 border-red-500/40',
  anaerobic: 'bg-rose-500/15 text-rose-400 border-rose-500/40',
  sprint: 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/40',
  intervals: 'bg-purple-500/15 text-purple-400 border-purple-500/40',
  ftp_test: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/40',
  ramp_test: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/40',
  test: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/40',
  race: 'bg-pink-500/15 text-pink-400 border-pink-500/40',
  group_ride: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40',
  skills: 'bg-teal-500/15 text-teal-400 border-teal-500/40',
  cross_training: 'bg-slate-500/15 text-slate-400 border-slate-500/40',
};

export function workoutChipClass(type: WorkoutType): string {
  return (
    _WORKOUT_CHIP[type] ??
    'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border)'
  );
}

/**
 * Compute the calendar date for a day_index given the block's start_date.
 *
 * Returns null if start_date is not yet set (block is still a draft).
 */
export function dateForDayIndex(
  block: Pick<TrainingBlockDetail, 'start_date'>,
  dayIndex: number,
): Date | null {
  if (!block.start_date) return null;
  const d = new Date(block.start_date);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + dayIndex);
  return d;
}

export function formatBlockDay(date: Date | null): string {
  if (date === null) return '—';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateRange(
  start: string | null,
  end: string | null,
): string {
  if (!start || !end) return '—';
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} → ${end}`;
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const sFmt = s.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameMonth ? undefined : 'numeric',
  });
  const eFmt = e.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${sFmt} → ${eFmt}`;
}

/**
 * Decide what visual state a planned day is in *for now*.
 *
 * Phase 5 will compute on-target / off-target adherence; phase 4
 * limits itself to the four states it can know just from start_date,
 * today, and "did anything attach to this day?". The block-day API
 * doesn't yet ship execution rows in the day payload, so for v1 we
 * treat "any execution" as a future server-side flag the caller can
 * pass in.
 */
export type DayState = 'rest' | 'today' | 'future' | 'executed' | 'missed';

export function dayState(
  day: TrainingBlockDay,
  date: Date | null,
  hasExecution: boolean,
  today: Date = new Date(),
): DayState {
  if (day.is_rest_day) return 'rest';
  if (hasExecution) return 'executed';
  if (date === null) return 'future';
  const d0 = stripTime(date);
  const t0 = stripTime(today);
  if (d0.getTime() === t0.getTime()) return 'today';
  if (d0.getTime() < t0.getTime()) return 'missed';
  return 'future';
}

function stripTime(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

/**
 * Return the upcoming Monday (or ``now`` if today is Monday) as a
 * YYYY-MM-DD string in local time.
 *
 * Used as the smart default for the Activate-flow start-date picker on
 * the block detail page. Most training-block JSON files in this app
 * assume ``day_index 0 == Monday`` (the typical Mon-Sun week), so
 * defaulting to a Monday avoids the off-by-one that happens when a
 * Sunday import activates "today" and the first day lands on Sunday
 * instead of Monday.
 */
export function nextMondayIso(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // JS getDay(): Sun=0, Mon=1, …, Sat=6. (8 - day) % 7 maps to days
  // until the next Monday, returning 0 when today is Monday.
  const daysUntilMonday = (8 - d.getDay()) % 7;
  d.setDate(d.getDate() + daysUntilMonday);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function dayStateLabel(state: DayState): string {
  return {
    rest: 'Rest',
    today: 'Today',
    future: 'Planned',
    executed: 'Done',
    missed: 'Missed',
  }[state];
}

export function dayStateClass(state: DayState): string {
  return {
    rest: 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border)',
    today: 'bg-(--color-accent)/20 text-(--color-accent) border-(--color-accent)/50',
    future: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
    executed: 'bg-green-500/15 text-green-400 border-green-500/50',
    missed: 'bg-red-500/15 text-red-400 border-red-500/40',
  }[state];
}
